import { parseHtmlString, serializeHtmlToString } from '../html-parse-serialize';
import getEditableTextFields, { IndesignTextFieldsConfig, TextFieldsData } from './editableTextFields/getData';
import saveEditableTextFields from './editableTextFields/saveData';
import getEditableMedia, { IndesignMediaElementConfig, MediaElementDataAttribute } from './editableMedia/getData';
import { MediaElement } from '../editableMedia/typing';
import saveEditableMedia from './editableMedia/saveData';
import getEditableLinks, { LinkConfig } from '../editableLinks/getData';
import saveEditableLinks from '../editableLinks/saveData';
import getEditableButtons, { ButtonConfig, removeButtonActions } from './editableButtons/getData';
import saveEditableButtons from './editableButtons/saveData';
import getEditableIframes, { IframeConfig } from './editableIframes/getData';
import saveEditableIframes from './editableIframes/saveData';
import getEditableEnrichments, { EnrichmentConfig } from './editableEnrichments/getData';
import saveEditableEnrichments from './editableEnrichments/saveData';
import getEditableSliders, { SliderConfig, SlidersConfig } from './editableSliders/getData';
import saveEditableSliders from './editableSliders/saveData';
import getThemedItems, { ThemedItemConfig } from './themedItems/getData';
import { getBackgroundDeps, saveBackgroundDeps } from './backgroundDeps';
import { getVideoCoverImage, replaceVideosWithCoverImages } from '../videos';
import constants, { accessibleVersionTextElements } from './constants';
import { getParsedCss } from './css';
import { EditorType } from '../../constants';
import { EditableItem } from '../../selectors';
import {
	getResourceUrl,
	getNumberFromString,
	isEditableButtonLink,
	hasLockedParent,
	getPageRootElement,
} from '../helpers';
import { Api } from '../../../../typings/api';
import * as helpers from '../helpers';

interface PageSize {
	width: number;
	height: number;
}

export interface DuplicatedItemInSpreadConfig {
	page: number;
	id: string;
	className: string;
}

interface PageData extends PageSize {
	html: string;
	editableTextFields: TextFieldsData;
	editableMedia: IndesignMediaElementConfig[];
	editableLinks: LinkConfig[];
	editableButtons: ButtonConfig[];
	editableIframes: IframeConfig[];
	editableEnrichments: EnrichmentConfig[];
	editableSliders: SliderConfig[];
	themedItems: ThemedItemConfig[];
	duplicatedItemsInSpread: DuplicatedItemInSpreadConfig[];
	isError: boolean;
	backgroundDeps?: string[];
}

interface EditableItemsData {
	[key: string]: EditableItem[];
}

const getPageSize = (htmlDocument: Document): PageSize => {
	const pageRoot = getPageRootElement(htmlDocument);
	// a page wrapped in an outer div repeats its size on that div, so the first div with a style
	// attribute is no longer the page itself
	const wrapperDiv = (pageRoot && pageRoot.style.width
		? pageRoot
		: htmlDocument.querySelector('div[style]')) as HTMLElement;

	if (!wrapperDiv) {
		return { width: 1000, height: 1000 };
	}
	const { style: { width, height } } = wrapperDiv;
	return {
		width: getNumberFromString(width as string),
		height: getNumberFromString(height as string),
	};
};

const updateUrlsInPageHtml = (htmlDocument: Document, config: Api.Config, resourceId: number): Document => {
	const elementsWithSrc = [...htmlDocument.querySelectorAll(`[src]:not([class*=${constants.iframeClassName}])`)];

	elementsWithSrc.forEach((element: MediaElement) => {
		const url = getResourceUrl(element.src, config, resourceId);
		element.setAttribute('src', url);
	});

	return htmlDocument;
};

// A page can carry the rules that only it uses in a <style> block of its own. The editor reads text
// styles, item sizes and animation timings out of the css it is given, so it has to see those rules as
// well. The caller adds the result after the shared file, not before: a lookup keeps the first match,
// and the shared file lists a rule's own styles ahead of the device specific ones.
const parsePageCss = (htmlDocument: Document, config: Api.Config, resourceId: number): string => {
	const pageRoot = getPageRootElement(htmlDocument);
	// a fixed point to insert before, so blocks moved out of the head keep the order they were written in
	const insertionPoint = pageRoot && pageRoot.firstChild;

	return [...htmlDocument.querySelectorAll('style')]
		.map((styleElement) => {
			// only the body is written out for display, so a block the html parser moved into the head would
			// style the items the editor reads and not the page they sit on
			if (pageRoot && !htmlDocument.body.contains(styleElement)) {
				pageRoot.insertBefore(styleElement, insertionPoint);
			}

			const parsedCss = getParsedCss(config, styleElement.textContent || '', resourceId);
			// the page on screen reads its rules from the element, not from the string returned here
			styleElement.textContent = parsedCss;

			return parsedCss;
		})
		.join('\n');
};

const getAllEditableElementsSelector = (): string => {
	const editableElementsSelectors = [
		`[class*=${constants.textFieldClassName}]`,
		`[class*=${constants.imageClassName}]`,
		`[class*=${constants.videoClassName}]`,
		`[class*=${constants.animationClassName}]`,
		`[class*=${constants.iframeClassName}]`,
		`[class*=${constants.sliderClassName}]`,
	];

	return editableElementsSelectors
		.map(item => `${item}:not([class*=${constants.lockedClassName}])`)
		.join(',');
};

const getEditableElements = (htmlDocument: Document): HTMLElement[] => ([
	...htmlDocument.querySelectorAll(getAllEditableElementsSelector()),
] as HTMLElement[]);

const removeEditableElements = (htmlDocument: Document): Document => {
	const elements = getEditableElements(htmlDocument).filter(element => !hasLockedParent(element));

	elements.forEach((element) => {
		const isVideoOrIframe = [
			constants.videoClassName,
			constants.animationClassName,
			constants.iframeClassName,
		].some(className => element.className.includes(className));

		const elementToHide = isVideoOrIframe
			? element.closest(`[id*=${constants.videoOrIframeWrapperId}], [class*=${constants.videoOrIframeWrapperId}]`) || element
			: element.querySelector(`#${element.id} > *`);

		if (elementToHide) {
			elementToHide.parentNode!.removeChild(elementToHide);
		}
	});

	return htmlDocument;
};

const addStylesToLockedElements = (htmlDocument: Document): Document => {
	const elements = [
		...htmlDocument.querySelectorAll(`[class*=${constants.lockedClassName}]`),
	] as HTMLElement[];

	elements.forEach((element) => {
		element.style.overflow = 'visible';
		const iconWrapper = htmlDocument.createElement('div');
		iconWrapper.className = constants.lockedIconClassName;
		const iconWrapperStyle = `
			position:absolute;
			bottom:0;
			right:0;
			display:flex;
			align-items:center;
			justify-content:center;
			width:24px;
			height:24px;
			border-radius:50%;
			transform-origin:bottom right;
		`;
		iconWrapper.setAttribute('style', iconWrapperStyle);

		const icon = htmlDocument.createElement('svg');
		const iconStyle = 'width:100%;height:100%;';
		icon.setAttribute('style', iconStyle);

		const useElement = htmlDocument.createElement('use');
		useElement.setAttribute('xlink:href', '#svgicon-iconset_uprez_padlock');

		icon.appendChild(useElement);
		iconWrapper.appendChild(icon);

		const isVideo = element.className.includes(constants.videoClassName);
		const isMultiStateObject = element.className.includes(constants.multiStateObjectClassName);

		if (isVideo) {
			const { parentNode } = element;

			if (parentNode) {
				const videoWrapper = element.closest(`[id*=${constants.videoOrIframeWrapperId}], [class*=${constants.videoOrIframeWrapperId}]`);
				const { image, defaultImage } = getVideoCoverImage(element, htmlDocument);

				if (videoWrapper) {
					videoWrapper.classList.add(constants.lockedClassName);
				}

				parentNode.removeChild(element);
				parentNode.appendChild(image);
				parentNode.appendChild(defaultImage);
				parentNode.appendChild(iconWrapper);
			}
		} else if (isMultiStateObject) {
			const children = [...element.querySelectorAll(`[${constants.multiStateObjectDataAttribute}]`)];
			children.forEach(child => {
				child.appendChild(iconWrapper.cloneNode(true));
			});
		} else {
			element.appendChild(iconWrapper);
		}
	});

	return htmlDocument;
};

const parseLinks = (htmlDocument: Document): Document => {
	const linkElements = [...htmlDocument.querySelectorAll('a')];

	linkElements.forEach((linkElement) => {
		linkElement.removeAttribute('href');

		if (isEditableButtonLink(linkElement)) {
			const parentLinkElement = linkElement.closest(`[id*=${constants.parentContainerId}]`) as HTMLElement;

			if (parentLinkElement) {
				parentLinkElement.classList.add(constants.editableButtonLinkClassName);

				const lockedText = parentLinkElement.querySelector(`[class*=${constants.textFieldClassName}][class*=${constants.lockedClassName}]`);

				if (lockedText) {
					lockedText.className = lockedText.className.replace(constants.lockedClassName, '');
					parentLinkElement.classList.add(constants.lockedClassName);
				}
			}
		}
	});

	return htmlDocument;
};

const removeSliderPreviews = (htmlDocument: Document): Document => {
	const sliderElements = [...htmlDocument.querySelectorAll(`[class*=${constants.sliderClassName}]`)];

	sliderElements.forEach((sliderElement) => {
		sliderElement.innerHTML = '';
	});

	return htmlDocument;
};

const removeLinksToExternalCss = (htmlDocument: Document, externalCssRelUrl: string, externalThemesCssRelUrl?: string): Document => {
	const linkElements = [htmlDocument.querySelector(`link[href="${externalCssRelUrl}"]`)];

	if (externalThemesCssRelUrl) {
		linkElements.push(htmlDocument.querySelector(`link[href="${externalThemesCssRelUrl}"]`));
	}

	linkElements.forEach((linkElement) => {
		if (linkElement) {
			linkElement.parentNode!.removeChild(linkElement);
		}
	});

	return htmlDocument;
};

const wrapElements = (htmlDocument: Document): Document => {
	const body = htmlDocument.body;

	const pageContainer = htmlDocument.createElement('div');
	pageContainer.id = 'page-container';

	while (body.firstChild) {
		pageContainer.appendChild(body.firstChild);
	}

	body.appendChild(pageContainer);

	const editableElements = [...pageContainer.querySelectorAll(`${accessibleVersionTextElements}, img`)];

	editableElements.forEach((element, index) => {
		// Create editable wrapper (outer)
		const editableWrapper = htmlDocument.createElement('div');
		const isImage = element.tagName === 'IMG';

		editableWrapper.id = `doc-1__idContainer${index}`;

		if (isImage) {
			editableWrapper.className = `${constants.imageClassName}-${index}`;

			const stylesWrapper = htmlDocument.createElement('div');
			const originalStyle = (element as HTMLElement).getAttribute('style');

			if (originalStyle) {
				(element as HTMLElement).setAttribute(MediaElementDataAttribute.style, originalStyle);
			}

			element.parentNode!.insertBefore(editableWrapper, element);
			editableWrapper.appendChild(stylesWrapper);
			stylesWrapper.appendChild(element);
		} else {
			editableWrapper.className = `${constants.textFieldClassName}-${index}`;

			const stylesWrapper = htmlDocument.createElement('div');

			// Wrap the element with the styles wrapper first
			element.parentNode!.insertBefore(editableWrapper, element);
			editableWrapper.appendChild(stylesWrapper);
			stylesWrapper.appendChild(element);
		}
	});

	return htmlDocument;
};

export const getModifiedPageData = (
	page: number,
	pageIndexInSpread: number,
	htmlString: string,
	css: string,
	config: Api.Config,
	resourceId: number,
	isEditablePage: boolean,
	isModifiedFromBackoffice: boolean,
	isAccountManagerOrAdmin: boolean,
	totalPages: number,
	spreads: number[][],
	pageHeightFromConfig?: number,
	externalCssRelUrl?: string,
	externalThemesCssRelUrl?: string,
	isAccessible?: boolean,
): PageData => {
	const pageData = {
		html: htmlString,
		width: 0,
		height: 0,
		isError: false,
	} as PageData;

	// try catch block is used to avoid app breaking in case something went wrong in parsing of inDesign html document
	try {
		const defaultHtmlDocument = isAccessible ? wrapElements(parseHtmlString(htmlString)) : parseHtmlString(htmlString);
		let htmlDocument = isAccessible ? wrapElements(parseHtmlString(htmlString)) : parseHtmlString(htmlString);

		const pageCss = parsePageCss(htmlDocument, config, resourceId);
		const cssWithPageCss = pageCss ? `${css}\n${pageCss}` : css;

		if (isEditablePage) {
			htmlDocument = removeButtonActions(htmlDocument);
			htmlDocument = removeEditableElements(htmlDocument);
			htmlDocument = parseLinks(htmlDocument);
		}

		// this method should be called after removeEditableElements to not update urls on editable images/videos as they will be removed anyway
		htmlDocument = updateUrlsInPageHtml(htmlDocument, config, resourceId);
		htmlDocument = removeSliderPreviews(htmlDocument);

		if (externalCssRelUrl) {
			htmlDocument = removeLinksToExternalCss(htmlDocument, externalCssRelUrl, externalThemesCssRelUrl);
		}

		if (isEditablePage) {
			// this method should be called after updateUrlsInPageHtml as locked videos should be replaced with cover images with correct urls
			htmlDocument = addStylesToLockedElements(htmlDocument);
		}

		if (!isEditablePage) {
			htmlDocument = replaceVideosWithCoverImages(htmlDocument);
		}

		const { width, height } = getPageSize(htmlDocument);

		pageData.html = serializeHtmlToString(htmlDocument);
		pageData.width = width;
		pageData.height = pageHeightFromConfig || height;

		if (isEditablePage) {
			const editableTextFields = getEditableTextFields(page, pageIndexInSpread, defaultHtmlDocument, cssWithPageCss, isModifiedFromBackoffice, isAccessible);
			const editableMedia = getEditableMedia(page, pageIndexInSpread, defaultHtmlDocument, cssWithPageCss, isAccessible);
			const editableLinks = getEditableLinks(page, defaultHtmlDocument, EditorType.INDESIGN, spreads, isAccountManagerOrAdmin);
			const editableButtons = getEditableButtons(page, pageIndexInSpread, defaultHtmlDocument, isAccountManagerOrAdmin, totalPages, spreads);
			const editableIframes = getEditableIframes(page, defaultHtmlDocument);
			const editableEnrichments = getEditableEnrichments(page, defaultHtmlDocument, isAccountManagerOrAdmin);
			const editableSliders = getEditableSliders(page, pageIndexInSpread, defaultHtmlDocument, cssWithPageCss);
			const themedItems = getThemedItems(page, defaultHtmlDocument, isAccountManagerOrAdmin);
			pageData.editableTextFields = editableTextFields;
			pageData.editableMedia = editableMedia.items;
			pageData.editableLinks = editableLinks.items;
			pageData.editableButtons = editableButtons.items;
			pageData.editableIframes = editableIframes.items;
			pageData.editableEnrichments = editableEnrichments.items;
			pageData.editableSliders = editableSliders.items;
			pageData.themedItems = themedItems.items;
			pageData.duplicatedItemsInSpread = [
				...editableTextFields.duplicatedItemsInSpread,
				...editableMedia.duplicatedItemsInSpread,
				...editableButtons.duplicatedItemsInSpread,
				...editableSliders.duplicatedItemsInSpread,
			];
			pageData.isError =
				editableTextFields.isError ||
				editableMedia.isError ||
				editableLinks.isError ||
				editableButtons.isError ||
				editableIframes.isError ||
				editableEnrichments.isError ||
				editableSliders.isError ||
				themedItems.isError;

			pageData.backgroundDeps = getBackgroundDeps(htmlDocument);
		}
	} catch (error) {
		pageData.isError = true;
	}

	return pageData;
};

export const getPageHtmlToSave = (
	htmlString: string,
	{
		editableTextFields,
		editableMedia,
		editableLinks,
		editableButtons,
		editableIframes,
		editableEnrichments,
		editableSliders,
	}: EditableItemsData,
	pageSlidersData?: SlidersConfig,
	backgroundDeps?: string[],
): string => {
	const htmlDocument = parseHtmlString(htmlString);
	saveEditableTextFields(htmlDocument, editableTextFields as IndesignTextFieldsConfig[]);
	saveEditableMedia(htmlDocument, editableMedia as IndesignMediaElementConfig[]);
	saveEditableLinks(htmlDocument, editableLinks as LinkConfig[], EditorType.INDESIGN);
	saveEditableButtons(htmlDocument, editableButtons as ButtonConfig[]);
	saveEditableIframes(htmlDocument, editableIframes as IframeConfig[]);
	saveEditableEnrichments(htmlDocument, editableEnrichments as EnrichmentConfig[]);
	saveEditableSliders(htmlDocument, editableSliders as SliderConfig[], pageSlidersData);
	saveBackgroundDeps(htmlDocument, backgroundDeps);

	return serializeHtmlToString(htmlDocument);
};

/**
 * Processes the HTML string by replacing editable text fields as specified.
 *
 * @param htmlString - The original HTML string.
 * @param editableData - The data containing editable items.
 * @returns The modified HTML string.
 */
export const getAccessiblePageHtmlToSave = (
	htmlString: string,
	{ editableTextFields, editableMedia }: EditableItemsData
): string => {
	const htmlDocument = parseHtmlString(htmlString);

	// Filter only IndesignTextFieldsConfig items
	const indesignTextFields = editableTextFields.filter(
		(item): item is IndesignTextFieldsConfig =>
			(item as IndesignTextFieldsConfig).content !== undefined
	);

	const indesignImageFields = editableMedia.filter(
		(item): item is IndesignMediaElementConfig =>
			(item as IndesignMediaElementConfig).isVisible !== undefined
	);

	saveAccessibleEditableFields(htmlDocument, indesignTextFields, indesignImageFields);

	return serializeHtmlToString(htmlDocument);
};

const saveAccessibleEditableFields = (htmlDocument: Document, editableTextFields: IndesignTextFieldsConfig[], editableMediaFields: IndesignMediaElementConfig[]): Document => {
	const textElements = [...htmlDocument.querySelectorAll(accessibleVersionTextElements)];
	const imageElements = [...htmlDocument.querySelectorAll('img')];
	let editableTextIndex = 0;
	let editableImageIndex = 0;

	if (textElements && textElements.length > 0) {
		textElements.forEach((element) => {
			if (editableTextIndex < editableTextFields.length) {
				const editableField = editableTextFields[editableTextIndex];

				if (editableField && editableField.content) {
					const newContentDoc = parseHtmlString(editableField.content);

					const newElements = [...newContentDoc.querySelectorAll(accessibleVersionTextElements)];

					newElements.forEach((newElement, index) => {
						// Clone the new element to avoid moving it from the parsed document
						const clonedNewElement = newElement.cloneNode(true);
						// Insert the cloned new element before the original element
						element.parentNode!.insertBefore(clonedNewElement, element);
					});

					// Remove the original element after inserting new elements
					element.parentNode!.removeChild(element);
				}

				editableTextIndex++;
			}
		});
	}

	if (imageElements && imageElements.length > 0) {
		imageElements.forEach((element) => {
			if (editableImageIndex < editableMediaFields.length) {
				const editableField = editableMediaFields[editableImageIndex];

				if (editableField) {
					// Restore original style attribute
					if (editableField.originalStyle) {
						element.setAttribute('style', editableField.originalStyle);
					}

					if (editableField.isVisible !== undefined) {
						if (!editableField.isVisible) {
							element.setAttribute('hidden', 'true');
							helpers.setDataAttributes(element, {
								[MediaElementDataAttribute.hidden]: true,
								[MediaElementDataAttribute.defaultLink]: editableField.defaultLink,
							});
						} else {
							element.removeAttribute('hidden');
							element.removeAttribute(MediaElementDataAttribute.hidden);
							element.removeAttribute(MediaElementDataAttribute.defaultLink);
						}
					}

					if (editableField.altAttr !== undefined) {
						element.setAttribute('alt', editableField.altAttr);
					}

					if (editableField.driveLink) {
						element.setAttribute('src', editableField.driveLink);
					}
					// Clean up temporary data attribute
					element.removeAttribute(MediaElementDataAttribute.style);
				}

				editableImageIndex++;
			}
		});
	}

	return htmlDocument;
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/utils/indesignEditor/html.ts